import { useT } from "../lib/i18n.jsx";
import { useToast } from "../components/Toast.jsx";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api, getToken } from "../lib/api.js";
import { IconCheck, IconX, IconArrow } from "../components/icons.jsx";

function fmtSize(bytes) {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  return mb < 1 ? `${(bytes / 1024).toFixed(0)} KB` : `${mb.toFixed(1)} MB`;
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toISOString().slice(0, 16).replace("T", " ");
}

export default function AdminReleasesPage() {
  const { t } = useT();
  const toast = useToast();
  const [releases, setReleases] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);

  async function load() {
    const r = await api("/admin/releases");
    setReleases(r.releases);
  }

  useEffect(() => {
    load().catch((err) => toast.error(err.message));
  }, []);

  async function setCurrent(id) {
    try {
      await api(`/admin/releases/${id}/current`, { method: "POST" });
      await load();
      toast.success("Set as current.");
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function remove(id) {
    if (!confirm("Delete this release? Users who already downloaded it are not affected.")) return;
    try {
      await api(`/admin/releases/${id}`, { method: "DELETE" });
      await load();
      toast.success("Deleted.");
    } catch (err) {
      toast.error(err.message);
    }
  }

  if (!releases) return <div className="loading">Loading releases</div>;

  return (
    <>
      <div className="ph">
        <div className="ph-title">
          <div className="eyebrow">Administration</div>
          <h1 className="h1">Client releases</h1>
        </div>
        <div className="ph-meta">{releases.length} total</div>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className={"chip " + (message.error ? "err" : "ok")}
          style={{ marginBottom: 18, padding: "10px 14px", fontSize: 12 }}
        >
          {message.error || message.ok}
        </motion.div>
      )}

      <UploadForm
        onUploaded={async () => {
          await load();
          toast.success("Uploaded.");
        }}
        setMessage={setMessage}
        setUploading={setUploading}
        uploading={uploading}
      />

      <div className="section">
        <div className="section-head">
          <h2>All releases</h2>
        </div>
        <div className="section-body" style={{ padding: 0 }}>
          {releases.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center" }}>
              <span className="serif" style={{ fontStyle: "italic", fontSize: 18, color: "var(--text-mute)" }}>
                no releases uploaded yet
              </span>
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Platform</th>
                  <th>Version</th>
                  <th>Filename</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {releases.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <span className="mono">{r.platform}</span>
                      <span className="dim" style={{ marginLeft: 6, fontSize: 11 }}>/ {r.arch}</span>
                    </td>
                    <td className="mono">v{r.version}</td>
                    <td>
                      <div style={{ fontSize: 13 }}>{r.filename}</div>
                      <div className="mono" style={{ fontSize: 10, color: "var(--text-faint)", marginTop: 3, wordBreak: "break-all" }}>
                        {r.sha256.slice(0, 24)}…
                      </div>
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>{fmtSize(r.size_bytes)}</td>
                    <td className="mono" style={{ fontSize: 11, color: "var(--text-dim)" }}>
                      {fmtDate(r.uploaded_at)}
                      {r.uploader_email && (
                        <div style={{ fontSize: 10, color: "var(--text-faint)" }}>
                          {r.uploader_email}
                        </div>
                      )}
                    </td>
                    <td>
                      {r.is_current ? (
                        <span className="chip ok"><IconCheck size={10} /> current</span>
                      ) : (
                        <span className="chip">archived</span>
                      )}
                    </td>
                    <td style={{ display: "flex", gap: 4 }}>
                      {!r.is_current && (
                        <button className="btn ghost" onClick={() => setCurrent(r.id)}>
                          Make current
                        </button>
                      )}
                      <button className="btn ghost danger" onClick={() => remove(r.id)}>
                        <IconX size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

function UploadForm({ onUploaded, setMessage, setUploading, uploading }) {
  const fileRef = useRef(null);
  const [platform, setPlatform] = useState("windows");
  const [arch, setArch] = useState("amd64");
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [makeCurrent, setMakeCurrent] = useState(true);
  const [isCritical, setIsCritical] = useState(false);
  const [progress, setProgress] = useState(null);

  async function upload(e) {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error("Pick a file first.");
      return;
    }
    if (!version) {
      toast.error("Set a version (e.g. 0.1.0).");
      return;
    }

    setUploading(true);
    setProgress(0);
    setMessage(null);

    try {
      const buf = await file.arrayBuffer();
      const params = new URLSearchParams({
        platform,
        arch,
        version,
        filename: file.name,
        make_current: makeCurrent ? "true" : "false",
        is_critical: isCritical ? "true" : "false",
      });
      if (notes) params.set("notes", notes);

      const url = `/api/admin/releases?${params.toString()}`;
      // Use XHR for upload progress
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url);
        xhr.setRequestHeader("Authorization", `Bearer ${getToken()}`);
        xhr.setRequestHeader("Content-Type", "application/octet-stream");
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.responseText);
          } else {
            try {
              const d = JSON.parse(xhr.responseText);
              reject(new Error(d.error || `HTTP ${xhr.status}`));
            } catch {
              reject(new Error(`HTTP ${xhr.status}`));
            }
          }
        };
        xhr.onerror = () => reject(new Error("network error"));
        xhr.send(buf);
      });

      onUploaded();
      setVersion("");
      setNotes("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      setProgress(null);
    }
  }

  return (
    <div className="section">
      <div className="section-head">
        <h2>Upload new release</h2>
      </div>
      <div className="section-body">
        <form onSubmit={upload}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
            <div className="field">
              <label>Platform</label>
              <select value={platform} onChange={(e) => setPlatform(e.target.value)} disabled={uploading}>
                <option value="windows">Windows</option>
                <option value="linux">Linux</option>
                <option value="macos">macOS</option>
              </select>
            </div>
            <div className="field">
              <label>Architecture</label>
              <select value={arch} onChange={(e) => setArch(e.target.value)} disabled={uploading}>
                <option value="amd64">amd64</option>
                <option value="arm64">arm64</option>
                <option value="386">386</option>
              </select>
            </div>
            <div className="field">
              <label>Version</label>
              <input
                type="text"
                placeholder="0.1.0"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                disabled={uploading}
              />
            </div>
          </div>

          <div className="field">
            <label>Installer file</label>
            <input ref={fileRef} type="file" disabled={uploading} />
            <span className="hint">
              For Windows: .exe (NSIS installer). For Linux: .AppImage or .deb. Max 200 MB.
            </span>
          </div>

          <div className="field">
            <label>Release notes (optional)</label>
            <input
              type="text"
              placeholder="Initial release / bugfixes / etc."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={uploading}
            />
          </div>

          <div className="setting-row" style={{ padding: "8px 0" }}>
            <div className="label-block">
              <div className="lbl">Make current after upload</div>
              <div className="key">replaces the active version for this platform/arch</div>
            </div>
            <div>
              <div
                className={"toggle " + (makeCurrent ? "on" : "")}
                onClick={() => !uploading && setMakeCurrent(!makeCurrent)}
                role="switch"
                aria-checked={makeCurrent}
              >
                <div className="knob" />
              </div>
            </div>
          </div>

          <div className="setting-row" style={{ padding: "8px 0" }}>
            <div className="label-block">
              <div className="lbl">Mark as critical update</div>
              <div className="key">prominent red banner for users — security fixes, breaking changes</div>
            </div>
            <div>
              <div
                className={"toggle " + (isCritical ? "on" : "")}
                onClick={() => !uploading && setIsCritical(!isCritical)}
                role="switch"
                aria-checked={isCritical}
              >
                <div className="knob" />
              </div>
            </div>
          </div>

          {progress !== null && (
            <div style={{ margin: "16px 0", height: 4, background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 2, overflow: "hidden" }}>
              <div style={{
                height: "100%",
                width: `${progress}%`,
                background: "var(--acc)",
                transition: "width 200ms",
              }} />
            </div>
          )}

          <button className="btn primary lg" type="submit" disabled={uploading}>
            {uploading ? `Uploading… ${progress ?? 0}%` : "Upload release"} <IconArrow size={14} />
          </button>
        </form>
      </div>
    </div>
  );
}
